import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  paymentReconciliationApi,
  type BalanceReportRow,
  type ReconciliationActivityRow,
} from "@/lib/api/payment-reconciliation";
import { formatCurrency } from "./reconciliation-shared";

export function CheckBalanceTab() {
  const [reportType, setReportType] = useState<"month" | "year" | "custom">("month");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [start, setStart] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [end, setEnd] = useState(() => {
    const d = new Date();
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return last.toISOString().slice(0, 10);
  });
  const [feePercent, setFeePercent] = useState("2.9");
  const [perTransactionFee, setPerTransactionFee] = useState("0.30");
  const [actualSquareCollected, setActualSquareCollected] = useState("");
  const [actualSquareRefund, setActualSquareRefund] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<{
    systemGross: number;
    expectedSquare: number;
    actualSquare: number | null;
    difference: number | null;
  } | null>(null);
  const [rows, setRows] = useState<BalanceReportRow[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentReconciliationApi.getBalance({
        reportType,
        month,
        start,
        end,
        feePercent: parseFloat(feePercent) || 0,
        perTransactionFee: parseFloat(perTransactionFee) || 0,
        actualSquareCollected:
          actualSquareCollected === "" ? null : parseFloat(actualSquareCollected),
        actualSquareRefund: parseFloat(actualSquareRefund) || 0,
      });
      setSummary(res.data.summary);
      setRows(res.data.rows);
    } catch {
      toast.error("Failed to load balance report");
    } finally {
      setLoading(false);
    }
  }, [
    actualSquareCollected,
    actualSquareRefund,
    end,
    feePercent,
    month,
    perTransactionFee,
    reportType,
    start,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const downloadReport = async () => {
    try {
      const alerts = await paymentReconciliationApi.getAlerts();
      const mismatchRows = alerts.data.rows;
      const csv = [
        [
          "Student",
          "Email",
          "Phone",
          "Invoice",
          "Amount",
          "School Status",
          "Square Status",
          "Difference Reason",
          "Recommended Action",
        ].join(","),
        ...mismatchRows.map((r: ReconciliationActivityRow) =>
          [
            r.student,
            r.email,
            r.phone,
            r.invoice,
            r.amount,
            r.school,
            r.square,
            `"${r.reason}"`,
            `"${r.action}"`,
          ].join(",")
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "payment-difference-report.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download report");
    }
  };

  const diff = summary?.difference;
  const diffClass = diff === null || diff === undefined || diff >= 0 ? "pr-diff-positive" : "pr-diff-negative";

  return (
    <div className="pr-card">
      <div className="pr-card-head">
        <div>
          <h2>Check Balance</h2>
          <p>Enter Square actuals manually, adjust Square processing fees, and run a difference report.</p>
        </div>
        <button type="button" className="pr-primary-btn" onClick={() => void downloadReport()}>
          Download Difference Report
        </button>
      </div>

      <div className="pr-balance-controls">
        <div className="pr-field">
          <label>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value as typeof reportType)}
          >
            <option value="month">Month</option>
            <option value="year">Full Year</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        <div className="pr-field">
          <label>Month</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <div className="pr-field">
          <label>From</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div className="pr-field">
          <label>To</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>

      <div className="pr-manual-controls">
        <div className="pr-field">
          <label>Transaction Fee %</label>
          <input
            type="number"
            step="0.01"
            value={feePercent}
            onChange={(e) => setFeePercent(e.target.value)}
          />
        </div>
        <div className="pr-field">
          <label>Per Transaction Fee</label>
          <input
            type="number"
            step="0.01"
            value={perTransactionFee}
            onChange={(e) => setPerTransactionFee(e.target.value)}
          />
        </div>
        <div className="pr-field">
          <label>Actual Square Collected</label>
          <input
            type="number"
            step="0.01"
            placeholder="Enter amount"
            value={actualSquareCollected}
            onChange={(e) => setActualSquareCollected(e.target.value)}
          />
        </div>
        <div className="pr-field">
          <label>Square Refund Amount</label>
          <input
            type="number"
            step="0.01"
            placeholder="Enter refund"
            value={actualSquareRefund}
            onChange={(e) => setActualSquareRefund(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : summary ? (
        <>
          <div className="pr-balance-summary">
            <div className="pr-money-card">
              <span>System Gross</span>
              <strong>{formatCurrency(summary.systemGross)}</strong>
            </div>
            <div className="pr-money-card">
              <span>Expected Square</span>
              <strong>{formatCurrency(summary.expectedSquare)}</strong>
            </div>
            <div className="pr-money-card">
              <span>Actual Square</span>
              <input
                className="pr-money-input"
                type="number"
                step="0.01"
                placeholder="Enter actual"
                value={actualSquareCollected}
                onChange={(e) => setActualSquareCollected(e.target.value)}
              />
            </div>
            <div className="pr-money-card">
              <span>Difference</span>
              <strong className={diffClass}>
                {diff === null || diff === undefined ? "—" : formatCurrency(diff)}
              </strong>
            </div>
          </div>

          <div className="pr-table-wrap">
            <div className="pr-toolbar">
              <strong>Balance Difference Detail</strong>
              <button type="button" className="pr-pill-btn" onClick={() => void load()}>
                Run Report
              </button>
            </div>
            <table className="pr-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>System Gross</th>
                  <th>System Refunds</th>
                  <th>Fee %</th>
                  <th>Per Charge Fee</th>
                  <th>Expected Square</th>
                  <th>Actual Square Entered</th>
                  <th>Square Refund Entered</th>
                  <th>Difference</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.period}>
                    <td>{row.period}</td>
                    <td>{formatCurrency(row.systemGross)}</td>
                    <td>{formatCurrency(row.systemRefunds)}</td>
                    <td>{row.feePercent.toFixed(2)}%</td>
                    <td>
                      {formatCurrency(row.perChargeFee)} × {row.chargeCount}
                    </td>
                    <td>{formatCurrency(row.expectedSquare)}</td>
                    <td>
                      {row.actualSquareEntered === null
                        ? "Manual entry required"
                        : formatCurrency(row.actualSquareEntered)}
                    </td>
                    <td>{formatCurrency(row.squareRefundEntered)}</td>
                    <td className={row.difference === null || row.difference >= 0 ? "pr-diff-positive" : "pr-diff-negative"}>
                      {row.difference === null ? "—" : formatCurrency(row.difference)}
                    </td>
                    <td>
                      <span
                        className={`pr-badge ${
                          row.status === "Matched"
                            ? "green"
                            : row.status === "Awaiting Entry"
                              ? "yellow"
                              : "red"
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
